import { apiCampaigns } from "../../axios/apiCampaignsInstance";
import { required, requireFields } from "../../utils/validation-campaigns.utils";
import { CAMPAIGN_DEFAULTS, UPLOAD_TYPES } from "../../components/apps/campaigns/constants";

export const createCampaign = async ({ username, apiKey, payload }) => {
  requireFields({
    username,
    api_key: apiKey,
    payload,
  });

  const {
    name,
    alias,
    description,
    id_template,
    id_templates,
    upload_contacts_type,
    programming_date,
    id_customer,
    contacts = [],
    file,
  } = payload;

  const hasMultipleTemplates = Array.isArray(id_templates) && id_templates.length > 0;

  requireFields({
    name,
    id_customer,
  });

  if (programming_date) {
    requireFields({ programming_date });
  }

  if (!hasMultipleTemplates) {
    requireFields({ id_template });
  }

  if (!upload_contacts_type || !Object.values(UPLOAD_TYPES).includes(upload_contacts_type)) {
    const valid = Object.values(UPLOAD_TYPES).join("', '");
    throw new Error(
      `upload_contacts_type must be one of: '${valid}'`
    );
  }

  const formData = new FormData();
  formData.append("name", name);
  if (alias) formData.append("alias", alias);
  if (description) formData.append("description", description);
  formData.append("id_template", String(hasMultipleTemplates ? id_templates[0] : id_template));
  if (hasMultipleTemplates) {
    formData.append("template_ids", JSON.stringify(id_templates));
  }
  formData.append("upload_contacts_type", upload_contacts_type);
  if (programming_date) formData.append("programming_date", programming_date);
  formData.append("id_customer", String(id_customer));

  if (upload_contacts_type === UPLOAD_TYPES.CONTACTS) {
    if (!Array.isArray(contacts) || contacts.length === 0) {
      throw new Error(`contacts array is required when upload_contacts_type='${UPLOAD_TYPES.CONTACTS}'`);
    }

    const phones = contacts
      .map((c) => c?.telefono || c?.phone)
      .filter(Boolean);

    if (phones.length === 0) {
      throw new Error('contacts must include at least one valid phone number');
    }

    phones.forEach((phone) => {
      formData.append('upload_contacts', String(phone));
    });
  }
  // Manejo de archivo (file y categories usan el mismo approach: subir archivo CSV)
  else if (upload_contacts_type === UPLOAD_TYPES.FILE || upload_contacts_type === UPLOAD_TYPES.CATEGORIES) {
    required(file, `file (required when upload_contacts_type='${upload_contacts_type}')`);
    formData.append('upload_contacts_file', file, file.name || 'contacts.csv');
  }


  // TODO: eliminar este bloque antes de subir cambio
  // try {
  // 	const base = api?.defaults?.baseURL || '(sin baseURL)';
  // 	const fullUrl = `${base}/?username=${username}&api_key=${api_key}`;
  // 	console.groupCollapsed('createCampaign → FormData');
  // 	console.log('URL:', fullUrl);
  // 	for (const [key, value] of formData.entries()) {
  // 		if (value instanceof File || value instanceof Blob) {
  // 			console.log(key, { name: value?.name, type: value?.type, size: value?.size });
  // 		} else {
  // 			console.log(key, value);
  // 		}
  // 	}
  // 	console.groupEnd();
  // } catch (_) {
  // }

  const response = await apiCampaigns.post('/', formData, {
    params: { username, api_key: apiKey },
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return response.data;
};


export const getCampaigns = async ({
  username,
  apiKey,
  page = 1,
  pageSize = 10,
  name_or_alias,
  status,
  dateFrom,
  dateTo,
}) => {
  requireFields({
    username,
    api_key: apiKey,
  });

  try {
    const params = {
      username,
      api_key: apiKey,
      page,
      page_size: pageSize,
    };

    if (name_or_alias && name_or_alias.trim()) {
      params.name_or_alias = name_or_alias.trim();
    }

    if (status && status !== 'todos') {
      params.status = status;
    }

     if (dateFrom) {
      params.date_from = dateFrom;
    }

    if (dateTo) {
      params.date_to = dateTo;
    }

    const { data } = await apiCampaigns.get('/', { params });

    return {
      campaigns: data?.campaigns || [],
      page: data?.page || page,
      pageSize: data?.page_size || pageSize,
      total: data?.total || 0,
      pages: data?.pages || 1,
    };
  } catch (error) {
    console.error('Error al obtener campañas:', error);
    throw error;
  }
};

export const normalizeCampaign = (campaign) => ({
  id: campaign.id,
  nombre: campaign.name ?? CAMPAIGN_DEFAULTS.nombre,
  alias: campaign.alias ?? CAMPAIGN_DEFAULTS.alias,
  template: campaign.id_template ?? CAMPAIGN_DEFAULTS.template,
  estado: campaign.status ?? CAMPAIGN_DEFAULTS.estado,
  fechaCreacion: campaign.created_at,
  descripcion: campaign.description,
  tipoContactos: campaign.upload_contacts_type,
  totalContactos: campaign.total_contacts ?? CAMPAIGN_DEFAULTS.totalContactos,
  fechaProgramacion: campaign.programming_date,
  archivoContactos: campaign.upload_contacts_file,
  idCustomer: campaign.id_customer,
});


export const deleteCampaign = async ({ campaignId, username, apiKey }) => {
  requireFields({
    campaign_id: campaignId,
    username,
    api_key: apiKey,
  });

  try {
    const { data } = await apiCampaigns.delete(`/${campaignId}`, {
      params: {
        username,
        api_key: apiKey,
      },
    });

    return data;
  } catch (error) {
    console.error('Error al eliminar campaña:', error);
    throw error;
  }
};


export const getCampaignById = async ({ campaignId, username, apiKey }) => {
  requireFields({
    campaign_id: campaignId,
    username,
    api_key: apiKey,
  });

  try {
    const { data } = await apiCampaigns.get(`/${campaignId}`, {
      params: {
        username,
        api_key: apiKey,
      },
    });

    return data;
  } catch (error) {
    console.error('Error al obtener campaña:', error);
    throw error;
  }
};

export const getCampaignMessages = async ({
  campaignId,
  username,
  apiKey,
  page = 1,
  pageSize = 100,
  phoneNumber,
  status,
}) => {
  required(campaignId, 'campaign_id');
  required(username, 'username');
  required(apiKey, 'api_key');

  try {
    const params = {
      username,
      api_key: apiKey,
      page,
      page_size: pageSize,
    };

    if (phoneNumber && phoneNumber.trim()) {
      params.phone_number = phoneNumber.trim();
    }

    if (status && status !== 'todos') {
      params.status = status.toLowerCase();
    }

    const { data } = await apiCampaigns.get(`/${campaignId}/messages`, { params });

    return {
      items: data.items || [],
      total: data.total || 0,
      pages: data.pages || 1,
      page: data.page || 1,
      size: data.size || pageSize,
    };
  } catch (error) {
    console.error('Error al obtener mensajes de campaña:', error);
    throw error;
  }
};


export const updateCampaign = async ({ campaignId, username, apiKey, data }) => {
  requireFields({
    campaign_id: campaignId,
    username,
    api_key: apiKey,
    data,
  });

  try {
    const response = await apiCampaigns.put(`/${campaignId}`, data, {
      params: {
        username,
        api_key: apiKey,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error al actualizar campaña:', error);
    throw error;
  }
};

export const launchCampaign = async ({ campaignId, username, apiKey }) => {
  requireFields({
    campaign_id: campaignId,
    username,
    api_key: apiKey,
  });

  try {
    const { data } = await apiCampaigns.post(`/${campaignId}/deploy`, null, {
      params: {
        username,
        api_key: apiKey,
      },
    });

    return data;
  } catch (error) {
    console.error('Error al lanzar campaña:', error);
    throw error;
  }
};

/**
 * POST /campaigns/validate-file
 * Envia archivo de contactos al servidor para validacion asyncrona.
 * El servidor crea un job, procesa en background y el frontend hace polling
 * hasta que el job se completa.
 *
 * @param {{ file: File, username: string, apiKey: string, onProgressChange?: (number) => void }}
 * @returns {Promise<{ valid_mx: number, valid_us: number, duplicates: number, rejected: number, total: number, rejected_file_url: string | null, estimated_templates_needed?: number }>}
 */
const POLL_INTERVAL_MS = 1000;
const MAX_POLL_TIME_MS = 120000; // 2 min timeout

export const validateContactFile = async ({ file, username, apiKey, onProgressChange }) => {
  requireFields({ file, username, api_key: apiKey });

  // 1. Enviar archivo — obtener job_id
  const formData = new FormData();
  formData.append('file', file);

  const { data: postData } = await apiCampaigns.post('/validate-file', formData, {
    params: { username, api_key: apiKey },
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  const { job_id } = postData;
  if (!job_id) {
    throw new Error('Backend no devolvio un job_id');
  }

  // 2. Hacer polling hasta que el job se complete o falle
  const startTime = Date.now();

  while (true) {
    if (Date.now() - startTime > MAX_POLL_TIME_MS) {
      throw new Error('Timeout: la validacion del archivo excedio el tiempo maximo');
    }

    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

    const { data: statusData } = await apiCampaigns.get(`/validate-file/status/${job_id}`, {
      params: { username, api_key: apiKey },
    });

    if (onProgressChange && typeof statusData.progress === 'number') {
      onProgressChange(statusData.progress);
    }

    if (statusData.status === 'completed') {
      return statusData.result;
    }

    if (statusData.status === 'error') {
      throw new Error(statusData.error || 'Error en la validacion del archivo');
    }

    // status: "queued" o "processing" — seguir polling
  }
};

/**
 * GET /campaigns/balance
 * Consulta saldo disponible, límite de sobregiro y estado de habilitación.
 */
export const getCampaignBalance = async ({ username, apiKey }) => {
  requireFields({ username, api_key: apiKey });

  const { data } = await apiCampaigns.get('/balance', {
    params: { username, api_key: apiKey },
  });

  return data;
};

/**
 * Descargar reporte Excel de campaña desde Template Delivery Service
 */
export const downloadCampaignReport = async ({ campaignId, username, apiKey }) => {
  requireFields({ campaign_id: campaignId, username, api_key: apiKey });

  const BASE_URL = process.env.REACT_APP_TEMPLATE_DELIVERY_SERVICE_URI;

  if (!BASE_URL) {
    throw new Error('TEMPLATE_DELIVERY_SERVICE_URI no está configurada');
  }

  const url = `${BASE_URL}/campaigns/${campaignId}/report?username=${encodeURIComponent(username)}&api_key=${encodeURIComponent(apiKey)}`;

  try {
    const res = await fetch(url, { method: "POST" });

    if (!res.ok) {
      let errorMsg = `Error ${res.status}`;
      try {
        const errData = await res.json();
        errorMsg = errData.detail || errData.error || errorMsg;
      } catch {}
      throw new Error(errorMsg);
    }

    return await res.json();
  } catch (error) {
    console.error('Error descargando reporte:', error);
    throw error;
  }
};
